import { motion, AnimatePresence } from "framer-motion";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";

export type TutorialStep = 0 | 1 | 2 | 3;

interface Props {
  step: TutorialStep;
  onAdvance: () => void;
}

interface TooltipLayout {
  boxStyle: React.CSSProperties;
  arrowDir: "down" | "up-left" | "up-right";
}

// Layout only – text comes from i18n at render time
const LAYOUTS: Record<1 | 2 | 3, TooltipLayout> = {
  // Step 1: points at mic button (bottom center)
  // MicButton: bottom-40 (160px) + height 80px → top edge at 240px from bottom
  // Arrow is 10px tall → box bottom = 240px puts arrow tip exactly at mic button top
  1: {
    boxStyle: {
      bottom: "240px",
      left: "calc(50% - 110px)",
      width: "220px",
    },
    arrowDir: "down",
  },
  // Step 2: points at persona icon (top-right)
  // Header top = 10vh; persona button h=48px (w-12); right at px-4=16px
  // Arrow tip at button bottom = 10vh+48px; box below: top = 10vh+48px+10px(arrow)
  2: {
    boxStyle: {
      top: "calc(10vh + 58px)",  // header(10vh) + button(48px) + arrow(10px)
      right: "16px",
      width: "210px",
    },
    arrowDir: "up-right",
  },
  // Step 3: points at X close button (top-left)
  // Header top = 10vh; X button h=40px (w-10); left at px-4=16px
  // Arrow tip at button bottom = 10vh+40px; box below: top = 10vh+40px+10px(arrow)
  3: {
    boxStyle: {
      top: "calc(10vh + 50px)",  // header(10vh) + button(40px) + arrow(10px)
      left: "16px",
      width: "210px",
    },
    arrowDir: "up-left",
  },
};

/** Small triangle arrow rendered below or above the tooltip box */
function Arrow({ dir }: { dir: TooltipLayout["arrowDir"] }) {
  if (dir === "down") {
    return (
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: "-10px",
          width: 0,
          height: 0,
          borderLeft: "10px solid transparent",
          borderRight: "10px solid transparent",
          borderTop: "10px solid white",
        }}
      />
    );
  }
  if (dir === "up-left") {
    return (
      <div
        className="absolute"
        style={{
          top: "-10px",
          left: "20px",
          width: 0,
          height: 0,
          borderLeft: "10px solid transparent",
          borderRight: "10px solid transparent",
          borderBottom: "10px solid white",
        }}
      />
    );
  }
  // up-right
  return (
    <div
      className="absolute"
      style={{
        top: "-10px",
        right: "20px",
        width: 0,
        height: 0,
        borderLeft: "10px solid transparent",
        borderRight: "10px solid transparent",
        borderBottom: "10px solid white",
      }}
    />
  );
}

export function WhisperTutorialOverlay({ step, onAdvance }: Props) {
  const { t } = useAppSettings();

  if (step === 0) return null;

  const layout = LAYOUTS[step as 1 | 2 | 3];
  const title = t(`whisper.tutorial.step${step}.title`);
  const body  = t(`whisper.tutorial.step${step}.body`);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={step}
        className="fixed inset-0 z-[9000] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Semi-transparent background overlay — tappable to advance */}
        <div
          className="absolute inset-0 bg-black/30"
          style={{ pointerEvents: "auto" }}
          onClick={onAdvance}
        />

        {/* Tooltip bubble */}
        <motion.div
          key={`box-${step}`}
          className="absolute pointer-events-auto"
          style={layout.boxStyle}
          initial={{ opacity: 0, scale: 0.92, y: step === 1 ? 8 : -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          onClick={(e) => {
            e.stopPropagation();
            onAdvance();
          }}
        >
          <div className="relative bg-white rounded-2xl px-4 py-3 shadow-xl">
            <Arrow dir={layout.arrowDir} />
            <p className="text-[13px] font-bold text-gray-900 leading-snug mb-1">
              {title}
            </p>
            <p className="text-[12px] text-gray-500 leading-snug">
              {body}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
